import { Card } from '@/components/ui/Card.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { Edit, Trash } from 'lucide-react';
import useGetTourCustomFields from '@/features/dashboard/tourCustomFields/hooks/useGetTourCustomFields.ts';
import useDeleteCustomField from '@/features/dashboard/tourCustomFields/hooks/useDeleteCustomField.ts';
import useReorderCustomFields from '@/features/dashboard/tourCustomFields/hooks/useReorderCustomFields.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { tourCustomFieldsRoute } from '@/features/dashboard/tourCustomFields/routes/tourCustomFieldsRoute.ts';
import BaseCustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/BaseCustomFieldDefinitionDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { getFieldTypeConfig } from '@/features/dashboard/tourCustomFields/components/customFieldTypeConfig.tsx';
import {
  SortableList,
  SortableListItem,
  SortableListDragHandle,
} from '@/components/ui/SortableList.tsx';

const CustomFieldsSummary = ({
  openEditModal,
}: {
  openEditModal: (fieldDef: BaseCustomFieldDefinitionDto) => void;
}) => {
  const { tourId } = tourCustomFieldsRoute.useParams();
  const { data: customFields, isLoading } = useGetTourCustomFields(tourId);
  const { mutate: deleteField } = useDeleteCustomField(tourId);
  const { mutate: reorderFields } = useReorderCustomFields(tourId);
  const { confirmDeleteWithCallback } = confirmDialogUseHook();

  const handleRemoveField = (fieldId: string) => {
    confirmDeleteWithCallback({
      title: 'Usuń pole',
      description: 'Czy na pewno chcesz usunąć to pole?',
      confirmText: 'Usuń',
      loadingText: 'Usuwanie...',
      onConfirm: () => {
        deleteField(fieldId);
      },
    });
  };

  if (isLoading || !customFields) {
    return null;
  }

  return (
    <SortableList<BaseCustomFieldDefinitionDto>
      items={customFields}
      getId={(field) => field.id}
      getOrderIndex={(field) => field.orderIndex}
      onReorder={reorderFields}
      className="flex flex-col gap-1 bg-neutral-50 px-4 py-2"
    >
      {(customFieldDef) => (
        <SortableListItem id={customFieldDef.id} asChild>
          <Card className="flex flex-row items-center justify-between bg-basic-white p-1.5">
            <div className="flex flex-row items-center gap-2">
              <div className="flex flex-row items-center gap-1">
                <SortableListDragHandle />
                <div className="text-sm font-semibold text-base-text">
                  {customFieldDef.name}
                </div>
              </div>

              <div className="flex flex-row items-center gap-1">
                {customFieldDef.required && (
                  <Chip label="Obowiązkowe" tone="neutral" fill="soft" />
                )}
              </div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <CustomFieldTypeBadge type={customFieldDef.type} />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEditModal(customFieldDef)}
                  className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-700"
                  title="Edytuj opcję"
                >
                  <Edit className="size-4" />
                </button>
                <button
                  onClick={() => handleRemoveField(customFieldDef.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-red-50 hover:text-red-600"
                  title="Usuń opcję"
                >
                  <Trash className="size-4" />
                </button>
              </div>
            </div>
          </Card>
        </SortableListItem>
      )}
    </SortableList>
  );
};

const CustomFieldTypeBadge = ({ type }: { type: FieldType }) => {
  const config = getFieldTypeConfig(type);
  return (
    // Jedyna pigułka z ikoną — `Chip` przyjmuje sam napis, więc geometria
    // (kształt, stopień pisma, obrys) jest tu przepisana z `Chip`.
    <span className="inline-flex w-fit items-center gap-1 rounded-full border border-neutral-200 bg-basic-white px-2 py-0.5 text-xs font-medium leading-none text-neutral-700 [&>svg]:size-3 [&>svg]:text-neutral-600">
      {config.icon}
      {config.title}
    </span>
  );
};

export default CustomFieldsSummary;
